import React, { useCallback, useMemo, useRef, useState } from "react";
import { scopedThreadKey, scopeThreadRef, scopeProjectRef } from "@cmmd-forge/client-runtime";
import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import type { SidebarThreadSortOrder } from "@cmmd-forge/contracts/settings";
import { useUiStateStore } from "../../uiStateStore";
import { useThreadSelectionStore } from "../../threadSelectionStore";
import { useCopyToClipboard } from "~/hooks/useCopyToClipboard";
import { useSettings } from "~/hooks/useSettings";
import { readLocalApi } from "../../localApi";
import { isMacPlatform } from "../../lib/utils";
import { toastManager } from "../ui/toast";
import { sanitizePublicUiError } from "~/productSafeLabels";
import type { useNewThreadHandler } from "../../hooks/useHandleNewThread";
import type { useThreadActions } from "../../hooks/useThreadActions";
import type { SidebarProjectSnapshot } from "./SidebarProjectItem";
import type { SidebarThreadSummary } from "../../types";
import { useSidebarProjectThreadData } from "./useSidebarProjectThreadData";
import { useSidebarThreadHandlers } from "./useSidebarThreadHandlers";
import { useSidebarProjectNewThread } from "./useSidebarProjectNewThread";
import { useSidebarProjectRemoval } from "./useSidebarProjectRemoval";

export { destroyCmmdRuntimeForEnvironment } from "./sidebarProjectRemoval";

// ---------------------------------------------------------------------------
// useSidebarProjectItem — project-button logic + composition of sub-hooks
// ---------------------------------------------------------------------------

export interface UseSidebarProjectItemProps {
  project: SidebarProjectSnapshot;
  isThreadListExpanded: boolean;
  activeRouteThreadKey: string | null;
  newThreadShortcutLabel: string | null;
  handleNewThread: ReturnType<typeof useNewThreadHandler>["handleNewThread"];
  archiveThread: ReturnType<typeof useThreadActions>["archiveThread"];
  settleThread: ReturnType<typeof useThreadActions>["settleThread"];
  deleteThread: ReturnType<typeof useThreadActions>["deleteThread"];
  dragInProgressRef: React.MutableRefObject<boolean>;
  suppressProjectClickAfterDragRef: React.MutableRefObject<boolean>;
  suppressProjectClickForContextMenuRef: React.MutableRefObject<boolean>;
  isManualProjectSorting: boolean;
  excludedThreadKeys?: ReadonlySet<string>;
  readonly threadSource?: readonly SidebarThreadSummary[] | undefined;
  onLinkCmmdProject?: () => void;
}

interface ProjectArchiveTarget {
  readonly thread: SidebarThreadSummary;
  readonly threadRef: ScopedThreadRef;
}

interface ProjectArchiveResult {
  readonly failedCount: number;
  readonly succeededKeys: readonly string[];
}

function collectProjectArchiveTargets(
  orderedThreadKeys: readonly string[],
  threadByKey: ReadonlyMap<string, SidebarThreadSummary>,
): ProjectArchiveTarget[] {
  return orderedThreadKeys.flatMap((threadKey) => {
    const thread = threadByKey.get(threadKey);
    return thread ? [{ thread, threadRef: scopeThreadRef(thread.environmentId, thread.id) }] : [];
  });
}

async function archiveProjectTargets(
  targets: readonly ProjectArchiveTarget[],
  archiveThreadWithCleanup: ReturnType<typeof useSidebarThreadHandlers>["archiveThreadWithCleanup"],
): Promise<ProjectArchiveResult> {
  const succeededKeys: string[] = [];
  let failedCount = 0;
  for (const target of targets) {
    try {
      const archived = await archiveThreadWithCleanup(target.threadRef, target.thread);
      if (archived === false) break;
      succeededKeys.push(scopedThreadKey(target.threadRef));
    } catch {
      failedCount += 1;
    }
  }
  return { failedCount, succeededKeys };
}

function reportProjectArchiveResult(result: ProjectArchiveResult): void {
  if (result.failedCount > 0) {
    toastManager.add({
      type: "error",
      title: `Failed to archive ${result.failedCount} thread${result.failedCount === 1 ? "" : "s"}`,
      description: "Some threads could not be archived. They remain selected so you can retry.",
    });
    return;
  }
  if (result.succeededKeys.length === 0) return;
  toastManager.add({
    type: "success",
    title:
      result.succeededKeys.length === 1
        ? "Archived 1 thread"
        : `Archived ${result.succeededKeys.length} threads`,
  });
}

export function useSidebarProjectItem(props: UseSidebarProjectItemProps) {
  const {
    project,
    isThreadListExpanded,
    activeRouteThreadKey,
    handleNewThread,
    archiveThread,
    settleThread,
    deleteThread,
    dragInProgressRef,
    suppressProjectClickAfterDragRef,
    suppressProjectClickForContextMenuRef,
    onLinkCmmdProject,
  } = props;

  const threadSortOrder = useSettings<SidebarThreadSortOrder>(
    (settings) => settings.sidebarThreadSortOrder,
  );
  const confirmThreadDelete = useSettings<boolean>((settings) => settings.confirmThreadDelete);
  const appSettingsConfirmThreadArchive = useSettings<boolean>(
    (settings) => settings.confirmThreadArchive,
  );
  const toggleProject = useUiStateStore((state) => state.toggleProject);
  const pinnedThreadById = useUiStateStore((state) => state.pinnedThreadById);
  const toggleThreadPinned = useUiStateStore((state) => state.toggleThreadPinned);
  const pinnedThreadKeys = useMemo(
    () =>
      new Set(
        Object.entries(pinnedThreadById)
          .filter(([, pinned]) => pinned)
          .map(([threadKey]) => threadKey),
      ),
    [pinnedThreadById],
  );
  const { handleCreateThreadClick, isAttachingDesktopRuntime, runtimeActionPolicy } =
    useSidebarProjectNewThread({ project, handleNewThread });

  const clearSelection = useThreadSelectionStore((state) => state.clearSelection);
  const toggleThreadSelection = useThreadSelectionStore((state) => state.toggleThread);
  const rangeSelectTo = useThreadSelectionStore((state) => state.rangeSelectTo);
  const removeFromSelection = useThreadSelectionStore((state) => state.removeFromSelection);
  const setSelectionAnchor = useThreadSelectionStore((state) => state.setAnchor);
  const selectedThreadCount = useThreadSelectionStore((state) => state.selectedThreadKeys.size);
  const { copyToClipboard: copyPathToClipboard } = useCopyToClipboard<{ path: string }>({
    onCopy: (ctx) => {
      toastManager.add({ type: "success", title: "Path copied", description: ctx.path });
    },
    onError: (error) => {
      toastManager.add({
        type: "error",
        title: "Failed to copy path",
        description: sanitizePublicUiError(error, "An error occurred."),
      });
    },
  });

  // Rename / confirm-archive UI state
  const [renamingThreadKey, setRenamingThreadKey] = useState<string | null>(null);
  const [renamingTitle, setRenamingTitle] = useState("");
  const [confirmingArchiveThreadKey, setConfirmingArchiveThreadKey] = useState<string | null>(null);
  const renamingCommittedRef = useRef(false);
  const renamingInputRef = useRef<HTMLInputElement | null>(null);

  const confirmArchiveButtonRefs = useRef(new Map<string, HTMLButtonElement>());

  // Thread data sub-hook
  const threadData = useSidebarProjectThreadData({
    project,
    threadSortOrder,
    isThreadListExpanded,
    activeRouteThreadKey,
    ...(props.excludedThreadKeys ? { excludedThreadKeys: props.excludedThreadKeys } : {}),
    ...(props.threadSource ? { threadSource: props.threadSource } : {}),
  });

  // Removing the project: its dialog, its progress, its retry. Split out
  // because a cloud removal is a durable CMMD operation that outlives this
  // row, so its state arrives from two places at once.
  const removal = useSidebarProjectRemoval({
    project,
    projectThreads: threadData.projectThreads,
  });
  // A useState setter, so stable — the context menu's "delete" item opens the
  // same dialog the row's own button does.
  const { setRemoveProjectDialogOpen } = removal;

  // Thread-row handlers sub-hook
  const threadHandlers = useSidebarThreadHandlers({
    projectCwd: project.cwd,
    confirmThreadDelete,
    deleteThread,
    archiveThread,
    settleThread,
    sidebarThreadByKeyRef: threadData.sidebarThreadByKeyRef as React.RefObject<
      ReadonlyMap<string, SidebarThreadSummary>
    >,
    clearSelection,
    setSelectionAnchor,
    toggleThreadSelection,
    rangeSelectTo,
    removeFromSelection,
    setRenamingThreadKey,
    setRenamingTitle,
    // "New thread on <branch>" seeds the draft with the row's branch. Only this
    // caller owns a new-thread handler, so only here does the menu offer it.
    startNewThreadOnBranch: (branch: string) => {
      void handleNewThread(scopeProjectRef(project.environmentId, project.id), { branch });
    },
    renamingCommittedRef,
  });
  const { archiveThreadWithCleanup } = threadHandlers;

  // ---------------------------------------------------------------------------
  // Project-button callbacks
  // ---------------------------------------------------------------------------

  const handleProjectButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (suppressProjectClickForContextMenuRef.current) {
        suppressProjectClickForContextMenuRef.current = false;
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (dragInProgressRef.current) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (suppressProjectClickAfterDragRef.current) {
        suppressProjectClickAfterDragRef.current = false;
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (selectedThreadCount > 0) clearSelection();
      toggleProject(project.projectKey);
    },
    [
      toggleProject,
      clearSelection,
      dragInProgressRef,
      project.projectKey,
      selectedThreadCount,
      suppressProjectClickAfterDragRef,
      suppressProjectClickForContextMenuRef,
    ],
  );

  const handleProjectButtonKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      if (dragInProgressRef.current) return;
      toggleProject(project.projectKey);
    },
    [toggleProject, dragInProgressRef, project.projectKey],
  );

  const handleProjectButtonPointerDownCapture = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      suppressProjectClickForContextMenuRef.current = false;
      if (
        event.button === 2 ||
        (event.button === 0 && event.ctrlKey && isMacPlatform(navigator.platform))
      ) {
        event.stopPropagation();
      }
      suppressProjectClickAfterDragRef.current = false;
    },
    [suppressProjectClickAfterDragRef, suppressProjectClickForContextMenuRef],
  );

  const handleArchiveAllProjectThreads = useCallback(async () => {
    const archiveTargets = collectProjectArchiveTargets(
      threadData.orderedProjectThreadKeys,
      threadData.sidebarThreadByKeyRef.current,
    );
    if (archiveTargets.length === 0) {
      toastManager.add({
        type: "warning",
        title: "No threads to archive",
        description: "This project has no visible threads.",
      });
      return;
    }

    const result = await archiveProjectTargets(archiveTargets, archiveThreadWithCleanup);
    if (result.succeededKeys.length > 0) removeFromSelection(result.succeededKeys);
    reportProjectArchiveResult(result);
  }, [
    removeFromSelection,
    archiveThreadWithCleanup,
    threadData.orderedProjectThreadKeys,
    threadData.sidebarThreadByKeyRef,
  ]);

  const showProjectActions = useCallback(
    (position: { readonly x: number; readonly y: number }) => {
      void (async () => {
        const api = readLocalApi();
        if (!api) return;
        const clicked = await api.contextMenu.show(runtimeActionPolicy.contextMenuItems, position);
        if (clicked === "link-cmmd") {
          onLinkCmmdProject?.();
          return;
        }
        if (clicked === "copy-path") {
          copyPathToClipboard(project.cwd, { path: project.cwd });
          return;
        }
        if (clicked === "archive-all-threads") {
          await handleArchiveAllProjectThreads();
          return;
        }
        if (clicked !== "delete") return;
        setRemoveProjectDialogOpen(true);
      })();
    },
    [
      copyPathToClipboard,
      handleArchiveAllProjectThreads,
      onLinkCmmdProject,
      setRemoveProjectDialogOpen,
      project.cwd,
      runtimeActionPolicy.contextMenuItems,
    ],
  );

  const handleProjectButtonContextMenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      suppressProjectClickForContextMenuRef.current = true;
      showProjectActions({ x: event.clientX, y: event.clientY });
    },
    [showProjectActions, suppressProjectClickForContextMenuRef],
  );

  const handleProjectActionsClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      const bounds = event.currentTarget.getBoundingClientRect();
      showProjectActions({ x: bounds.right, y: bounds.bottom });
    },
    [showProjectActions],
  );

  const handleCopyProjectPathClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      copyPathToClipboard(project.cwd, { path: project.cwd });
    },
    [copyPathToClipboard, project.cwd],
  );

  return {
    // Thread data (delegated)
    ...threadData,
    // Rename / confirm-archive UI state
    renamingThreadKey,
    renamingTitle,
    setRenamingTitle,
    confirmingArchiveThreadKey,
    setConfirmingArchiveThreadKey,
    // Project removal (delegated)
    ...removal,
    renamingInputRef,
    renamingCommittedRef,
    confirmArchiveButtonRefs,
    appSettingsConfirmThreadArchive,
    pinnedThreadKeys,
    toggleThreadPinned,
    // Project-button callbacks
    clearSelection,
    handleProjectButtonClick,
    handleProjectButtonKeyDown,
    handleProjectButtonPointerDownCapture,
    handleProjectButtonContextMenu,
    handleProjectActionsClick,
    handleCopyProjectPathClick,
    handleCreateThreadClick,
    isConnecting: isAttachingDesktopRuntime,
    runtimeActionPolicy,
    // Thread-row callbacks (delegated)
    ...threadHandlers,
  };
}
